import { MutateOptions } from '@tanstack/query-core';
import { useAtomValue } from 'jotai';
import { QUOTE_PAYMENT_TYPES } from '../../../consts';
import { updateQuoteAtom } from '../../../store/global/atoms';
import { ApiResponse, updateQuoteParams } from '../../../ts';
import { QuotedItem } from 'src/ts/interfaces/quotes/quoted-item';
import { QUOTE_STAGE } from '../../../consts';

type Options = MutateOptions<ApiResponse<any>, Error, updateQuoteParams>;

export const useUpdateQuote = () => {
  const updateQuoteBase = useAtomValue(updateQuoteAtom);

  const updateQuote = {
    transfer: (quoteId: string, quotedItems: QuotedItem[], acceptedAmount: number, selectedTransferPaymentMethod: boolean, options?: Options) =>
      updateQuoteBase.mutate({ quoteId, paymentType: QUOTE_PAYMENT_TYPES.TRANSFER, quotedItems, acceptedAmount, selectedTransferPaymentMethod },options),
    receipt: (quoteId: string, numberOfPayments: string, acceptedAmount: number, payment1: number, selectedReceiptPaymentMethod: boolean, options?: Options,) => 
      updateQuoteBase.mutate(
        { quoteId, paymentType: QUOTE_PAYMENT_TYPES.RECEIPT, numberOfPayments, acceptedAmount, payment1, selectedReceiptPaymentMethod },
        options,
      ),
    card: (quoteId: string, quotedItems: QuotedItem[], numberOfPayments: string, acceptedAmount: number, payment1: number, selectedCardPaymentMethod: boolean, options?: Options) =>
      updateQuoteBase.mutate(
        { quoteId, paymentType: QUOTE_PAYMENT_TYPES.CARD, quotedItems, numberOfPayments, acceptedAmount, payment1, selectedCardPaymentMethod },
        options,
      ),
    acceptedInAppConsumer: (quoteId: string, acceptedInAppConsumer: boolean, acceptedAmount: number ) => {
      updateQuoteBase.mutate({ quoteId, acceptedInAppConsumer, quoteStage: QUOTE_STAGE.ACCEPTED, acceptedAmount });
    },
    reject: (quoteId: string, rejectedInAppConsumer: boolean, rejectionReason?: string) => {
      updateQuoteBase.mutate({ quoteId, rejectedInAppConsumer, quoteStage: QUOTE_STAGE.REJECTED, rejectionReason });
    },
    revise: (quoteId: string, requestedCallInAppConsumer: boolean) => {
      updateQuoteBase.mutate({ quoteId, requestedCallInAppConsumer });
    },
    audioPlayed: (quoteId: string, audioPlayedInAppConsumer: boolean) => {
      updateQuoteBase.mutate({ quoteId, audioPlayedInAppConsumer });
    },
    audioListened: (quoteId: string, audioListenedInAppConsumer: boolean) => {
      updateQuoteBase.mutate({ quoteId, audioListenedInAppConsumer });
    },
    accessToReportDetails: (quoteId: string, accessToReportDetails: boolean) => {
      updateQuoteBase.mutate({ quoteId, accessToReportDetails });
    },
    accessToAcceptQuote: (quoteId: string, accessToAcceptQuoteInAppConsumer: boolean) => {
      updateQuoteBase.mutate({ quoteId, accessToAcceptQuoteInAppConsumer });
    },
    accessToConsentSignForm: (quoteId: string, accessToConsentSignForm: boolean) => {
      updateQuoteBase.mutate({ quoteId, accessToConsentSignForm });
    },
    accessToConsentSignDocument: (quoteId: string, accessToConsentSignDocument: boolean) => {
      updateQuoteBase.mutate({ quoteId, accessToConsentSignDocument });
    },
    accessToPaymentMethod: (quoteId: string, accessToPaymentMethod: boolean) => {
      updateQuoteBase.mutate({ quoteId, accessToPaymentMethod });
    },
    accessToCardPaymentMethod: (quoteId: string, accessToCardPaymentMethod: boolean) => {
      updateQuoteBase.mutate({ quoteId, accessToCardPaymentMethod });
    },
    selectedCardPaymentMethod: (quoteId: string, selectedCardPaymentMethod: boolean) => {
      updateQuoteBase.mutate({ quoteId, selectedCardPaymentMethod });
    },
    accessToTransferPaymentMethod: (quoteId: string, accessToTransferPaymentMethod: boolean) => {
      updateQuoteBase.mutate({ quoteId, accessToTransferPaymentMethod });
    },
    accessToReceiptPaymentMethod: (quoteId: string, accessToReceiptPaymentMethod: boolean) => {
      updateQuoteBase.mutate({ quoteId, accessToReceiptPaymentMethod });
    },
     accessToSepaSignDocument: (quoteId: string, accessToSepaSignDocument: boolean) => {
      updateQuoteBase.mutate({ quoteId, accessToSepaSignDocument });
    },
  };

  return {
    updateQuoteResult: updateQuoteBase.data,
    updateQuote,
    isLoading: updateQuoteBase.isPending,
    error: updateQuoteBase.error,
  };
};
